import { useAccountStore } from "@/composables/account/useAccountStore";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import type { Account } from "@/types";
import { onMounted, onUnmounted, ref } from "vue";

/**
 * The logged-in account for a component: loads it on mount — sharing the store's cached request with
 * every other caller (see `useAccountStore`) — and then runs `onLoaded`, unless the component has
 * unmounted meanwhile. A failed load, or a failing `onLoaded`, goes to the global error handler.
 *
 * `accountData` is the store's account, so at setup it may still hold an earlier page's copy (or the
 * empty defaults); `onLoaded` is where to act on the account this page loaded. Changes go through the
 * store's actions.
 */
export function useAccount(
  onLoaded?: (accountData: Readonly<Account>) => void | Promise<void>
) {
  const accountStore = useAccountStore();
  const { globalErrorHandler } = useGlobalHandlers();
  // True once this page's account has arrived and `onLoaded` has run, so a gate can wait for it. A
  // failed load leaves it false: the store then still holds an earlier page's copy, or the defaults,
  // and a gate opening on that would show a view chosen for another account.
  const loaded = ref(false);
  // A page left before its account answered must not act on it: DashboardView would navigate back.
  let mounted = true;
  onUnmounted(() => {
    mounted = false;
  });

  onMounted(async () => {
    try {
      const account = await accountStore.load();
      try {
        if (mounted) await onLoaded?.(account);
      } finally {
        loaded.value = true;
      }
    } catch (error) {
      globalErrorHandler(error);
    }
  });

  return { accountData: accountStore.account, loaded };
}
